import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiClock, FiBarChart, FiExternalLink, FiBookOpen } = FiIcons;

const TutorialCard = ({ tutorial }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return '#00ff88';
      case 'intermediate':
        return '#ffa500';
      case 'advanced':
        return '#ff6b6b';
      default:
        return '#00ff88';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return '🟢';
      case 'intermediate':
        return '🟡';
      case 'advanced':
        return '🔴';
      default:
        return '🟢';
    }
  };

  // Generate YouTube thumbnail URL from video ID
  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  // Generate YouTube URL from video ID
  const getYouTubeUrl = (videoId) => {
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const thumbnailUrl = tutorial.videoId ? getYouTubeThumbnail(tutorial.videoId) : tutorial.thumbnail;
  const youtubeUrl = tutorial.videoId ? getYouTubeUrl(tutorial.videoId) : null;

  return (
    <motion.div 
      className="tool-card"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
      style={{
        overflow: 'hidden',
        wordWrap: 'break-word',
        hyphens: 'auto'
      }}
    >
      {/* Thumbnail */}
      {thumbnailUrl && (
        <div style={{
          width: '100%',
          height: '180px',
          backgroundImage: `url(${thumbnailUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: '12px',
          marginBottom: '16px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Play button overlay */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <SafeIcon 
              icon={FiPlay} 
              style={{ 
                color: '#ff8c00', 
                fontSize: '24px',
                marginLeft: '2px' // Center the play icon better
              }} 
            />
          </div>
          
          {/* Duration badge */}
          <div style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            background: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <SafeIcon icon={FiClock} style={{ fontSize: '12px' }} />
            {tutorial.duration}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="tool-header">
        <div className="tool-icon">🎓</div>
        <div className="tool-info" style={{ 
          minWidth: 0,
          flex: 1
        }}>
          <div 
            className="tool-title" 
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              fontSize: '18px',
              fontWeight: '700',
              marginBottom: '8px'
            }}
          >
            {tutorial.title}
          </div>
          
          {/* Tool and Category badges */}
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '8px'
          }}>
            <span style={{
              background: 'rgba(255, 140, 0, 0.2)',
              color: '#ff8c00',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              border: '1px solid rgba(255, 140, 0, 0.3)'
            }}>
              {tutorial.tool}
            </span>
            <span style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#b0b0b0',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              {tutorial.category}
            </span>
          </div>

          {/* Difficulty level */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>{getDifficultyIcon(tutorial.difficulty)}</span>
            <span style={{
              color: getDifficultyColor(tutorial.difficulty),
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {tutorial.difficulty}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <p 
        className="tool-description" 
        style={{ 
          marginBottom: '20px',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.5',
          maxHeight: '4.5em',
          color: '#b0b0b0'
        }}
      >
        {tutorial.description}
      </p>

      {/* What You'll Learn Preview */}
      <div style={{
        background: 'rgba(255, 140, 0, 0.1)',
        border: '1px solid rgba(255, 140, 0, 0.2)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <h4 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#ff8c00',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <SafeIcon icon={FiBarChart} style={{ fontSize: '14px' }} />
          What You'll Learn
        </h4>
        <p style={{
          fontSize: '13px',
          color: '#d0d0d0',
          lineHeight: '1.4',
          margin: 0
        }}>
          Master {tutorial.tool} fundamentals and build real-world projects with step-by-step guidance.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="tool-actions" style={{
        display: 'flex',
        gap: '8px',
        minWidth: 0
      }}>
        <Link
          to={`/tutorials/${tutorial.slug}`}
          className="btn btn-primary"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '8px',
            flex: '1',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            background: 'linear-gradient(135deg, #ff8c00 0%, #ffa500 100%)',
            border: 'none'
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Read Full Tutorial
          </span>
          <SafeIcon icon={FiBookOpen} style={{ 
            width: '16px', 
            height: '16px',
            flexShrink: 0 
          }} />
        </Link>
        
        {youtubeUrl && (
          <motion.a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '80px',
              flexShrink: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              border: '1px solid rgba(255, 140, 0, 0.3)',
              color: '#ff8c00'
            }}
            whileHover={{ 
              scale: 1.02,
              borderColor: '#ff8c00',
              boxShadow: '0 0 15px rgba(255, 140, 0, 0.3)'
            }}
            whileTap={{ scale: 0.98 }}
          >
            YouTube
          </motion.a>
        )}
      </div>
    </motion.div>
  );
};

export default TutorialCard;